import {
	ButtonBase,
	FileSelector,
	IconButton,
	PersonAvatar,
	PersonAvatarStatusValue
} from "client/Components";
import { setTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import styles from "./AvatarSelector.module.less";
import classNames from "classnames";
import { never } from "client_legacy/ndaw/errors";

export type AvatarPlaceholderValue = {
	kind: "initials";
	initials: string;
} | {
	kind: "user-icon";
	initials?: undefined;
};

export type AvatarSelectorProps = {
	avatarUrl: string | undefined;
	placeholder: AvatarPlaceholderValue;
	trackingId: string;
} & (
	{
		isReadonly: true;
		onChange?: undefined;
		supportedContentTypes?: undefined;
		status?: PersonAvatarStatusValue;
	}
	|
	{
		isReadonly: false;
		onChange(file: File): void;
		supportedContentTypes?: readonly string[];
		status?: undefined;
	}
);

export const AvatarSelector = (props: AvatarSelectorProps): ReactElement => {
	const {
		avatarUrl,
		trackingId,
		isReadonly,
		onChange,
		supportedContentTypes,
		status,
		placeholder
	} = props;

	const [isCircleHovered, setIsCircleHovered] = useState<boolean>(false);
	const [isAppendixHovered, setIsAppendixHovered] = useState<boolean>(false);

	const renderAvatar = (): ReactElement | null => {
		return (
			<PersonAvatar
				{...(
					placeholder.kind === "initials"
						? {
							placeholderKind: "initials",
							initials: placeholder.initials
						}
						: placeholder.kind === "user-icon"
							? {
								placeholderKind: "user-icon"
							}
							: never(placeholder)
				)}
				imageSrc={avatarUrl}
				size="xxx-large"
				trackingId="avatar"
				status={status}
			/>
		);
	};

	const changeHandler = useCallback(
		(file: File): void => {
			if (isReadonly) {
				console.error("Cannot change readonly avatar");
				return;
			}

			onChange(file);
		},
		[isReadonly, onChange]
	);

	if (isReadonly) {
		return (
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
			>
				{renderAvatar()}
			</div>
		);
	}

	return (
		<FileSelector
			onChange={changeHandler}
			contentTypes={supportedContentTypes}
		>
			{(onClick) => (
				<div
					{...setTrackingId(trackingId)}
					className={styles.root}
				>
					<ButtonBase
						onClick={onClick}
						className={classNames(
							styles.button,
							{
								[styles.button_forceHoverState]: isAppendixHovered
							}
						)}
						onMouseEnter={() => setIsCircleHovered(true)}
						onMouseLeave={() => setIsCircleHovered(false)}
						trackingId="circle"
					>
						<div className={styles.preserveParentHitBoxContainer}>
							{renderAvatar()}
							<div className={styles.overlay}>
								{localize("_Change")}
							</div>
						</div>
					</ButtonBase>
					<div
						className={styles.appendix}
						onMouseEnter={() => setIsAppendixHovered(true)}
						onMouseLeave={() => setIsAppendixHovered(false)}
					>
						<IconButton
							icon="camera"
							kind="secondary"
							trackingId="appendix"
							shape="round"
							size="large"
							isActive={isCircleHovered}
							onClick={onClick}
						/>
					</div>
				</div>
			)}
		</FileSelector>
	);
};
AvatarSelector.displayName = "AvatarSelector";